import React, { useEffect, useState } from 'react';
import { View, Linking } from 'react-native';
import { useDispatch } from 'react-redux';
import { useLinkTo } from '@react-navigation/native';

import Typography from 'src/components/Text/Text';
import Toast from 'src/components/Toast/Toast';
import CompanyLandingScreen from '../CompanyLandingScreen/CompanyLandingScreen';
import CompanyDetailsScreen from '../CompanyDetailsScreen/CompanyDetailsScreen';
import DotNumberScreen from '../DotNumberScreen/DotNumberScreen';
import GaragingAddressScreen from '../GaragingAddressScreen/GaragingAddressScreen';
import LeadScoreModal from '../../components/LeadScore/LeadScoreModal';
import SecondUserScreen from 'src/features/user/screens/SecondUser/SecondUserScreen';
import LoadingTruck from 'src/components/loadingTruck/loadingTruck';
import { styles } from './CompanyStartScreen.styles';
import { useLazyGetCompanyByDotMcQuery } from 'src/features/company/api/companyApi';
import {
  useGetRedirectUrlMutation,
  useCheckProducerAvailabilityMutation,
} from 'src/features/authentication/api/authenticationApi';
import { useTokenFlow, FlowResult } from 'src/features/authentication/hooks/useTokenFlow';
import { UserWithCompany } from '@typeRoots/common/user';
import { TP_LINKS } from 'src/constants/externalLinks';
import { Variant } from '@features/company/types/variant';
import { setAccountSource, setShowGlobalLoader } from 'src/slices/UiSlice';

const CompanyStartScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();

  // State for screens and modals
  const [currentScreen, setCurrentScreen] = useState<
    'landing' | 'dotNumberInput' | 'details' | 'leadScore' | 'garagingAddress' | null
  >(null);
  const [leadModalVisible, setLeadModalVisible] = useState(false);
  const [secondUserModalVisible, setSecondUserModalVisible] = useState(false);
  const [userInfo, setUserInfo] = useState<UserWithCompany | null>(null);
  const [dotNumberInput, setDotNumberInput] = useState('');
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Error states
  const [companyErrorToast, setCompanyErrorToast] = useState(false);
  const [userErrorToast, setUserErrorToast] = useState(false);

  // Selectors
  // const TPJwtToken = useSelector((state: RootState) => state.UI.tpJwtToken);

  // Hooks
  const { initializeFromToken, isLoading, error, resetReduxState } = useTokenFlow();
  const [getCompanyByDotMc] = useLazyGetCompanyByDotMcQuery();
  const [getRedirectUrl, { isLoading: redirectLoading, error: redirectError }] =
    useGetRedirectUrlMutation();
  const [checkProducerAvailability] = useCheckProducerAvailabilityMutation();

  // Initialize on mount
  useEffect(() => {
    resetReduxState();
    extractAndProcessToken();
  }, []);

  // Manage global loader to suppress LoadingBar when showing LoadingTruck
  useEffect(() => {
    const isAnyLoading = isLoading || redirectLoading || isRedirecting;
    dispatch(setShowGlobalLoader(isAnyLoading));

    // Cleanup: reset on unmount
    return () => {
      dispatch(setShowGlobalLoader(false));
    };
  }, [isLoading, redirectLoading, isRedirecting, dispatch]);

  /**
   * Extract token from URL and process the flow
   */
  const extractAndProcessToken = async () => {
    try {
      const url = await Linking.getInitialURL();

      if (!url || !url.includes('token=')) {
        window.location.href = TP_LINKS.TP_INSURANCE_URL;
        return;
      }

      const urlParams = new URL(url);
      const token = urlParams.searchParams.get('token');
      const accountSource = urlParams.searchParams.get('accountSource') || 'TRUCKER_PATH_APP';

      if (!token) {
        window.location.href = TP_LINKS.TP_INSURANCE_URL;
        return;
      }

      setJwtToken(token);
      dispatch(setAccountSource(accountSource));

      // Use the token flow hook
      const result = await initializeFromToken(token);

      // Handle the result
      handleFlowResult(result);
    } catch (err) {
      console.error('Error extracting token:', err);
      setUserErrorToast(true);
    }
  };

  /**
   * Handle the result from token flow
   */
  const handleFlowResult = (result: FlowResult) => {
    switch (result.type) {
      case 'dotNumberInput':
        setUserInfo(result.userInfo);
        setCurrentScreen('dotNumberInput');
        break;

      case 'companyDetails':
        setUserInfo(result.userInfo);
        setCurrentScreen('details');
        break;

      case 'companyLanding':
        setCurrentScreen('landing');
        if (result.leadScore === 'SENIOR_BROKER_TRACK') {
          setLeadModalVisible(true);
        }
        break;

      case 'secondUser':
        setUserInfo(result.userInfo);
        setSecondUserModalVisible(true);
        break;

      case 'garagingAddress':
        setUserInfo(result.userInfo);
        setCurrentScreen('garagingAddress');
        break;

      case 'redirect':
        window.location.href = result.url;
        break;

      case 'error':
        setCompanyErrorToast(true);
        break;

      // For other cases (quotesSuccess, insurance, etc.), navigation is handled in the hook
      default:
        break;
    }
  };

  const handleRedirectError = (error: unknown) => {
    console.error('Error during redirect:', error || redirectError);
    setCompanyErrorToast(true);
  };

  /**
   * Handle DOT number submission from DotNumberScreen
   */
  const handleDotNumberSubmit = async (dotVariant: Variant) => {
    try {
      setIsRedirecting(true);
      const newUser = {
        email: userInfo?.user.email || '',
        phone: userInfo?.user.phone || '',
        firstName: userInfo?.user.firstName || '',
        lastName: userInfo?.user.lastName || '',
      };
      const response = await getRedirectUrl({
        newUser,
        token: jwtToken || '',
        dotNumber: dotNumberInput,
      }).unwrap();

      if (dotVariant === 'v4v5') {
        const companyObject = await getCompanyByDotMc({
          dotNumber: dotNumberInput,
          mcNumber: '',
          tpJwt: jwtToken || '',
        }).unwrap();

        const leadScoreRange = companyObject.company?.leadScoreRange || '$22,000 - $28,000';

        linkTo(
          `/followup-screen?redirectUrl=${encodeURIComponent(response.redirectUrl)}&leadScoreRange=${encodeURIComponent(
            leadScoreRange,
          )}`,
        );
      } else {
        // Normal path for v1, v2, v5, v6
        window.location.href = response.redirectUrl;
      }
    } catch (error) {
      setIsRedirecting(false);
      handleRedirectError(error);
    }
  };

  /**
   * Handle "No DOT" submission from DotNumberScreen
   */
  const handleNoDotSubmit = async () => {
    // Trigger garaging address flow for Phase 2
    setCurrentScreen('garagingAddress');
  };

  /**
   * Handle garaging address submission
   * Calls backend to determine producer availability and route accordingly
   */
  const handleGaragingAddressSubmit = async (state: string) => {
    try {
      setIsRedirecting(true);

      // Call producer availability endpoint
      const response = await checkProducerAvailability({
        token: jwtToken || '',
        garagingState: state.toUpperCase(), // Uppercase state code
      }).unwrap();

      const { destination, redirectUrl } = response;

      if (destination === 'tpi') {
        // User is eligible for TPI flow - proceed to company details
        setIsRedirecting(false);
        linkTo(`/company-details/?token=${jwtToken}&garagingState=${state}`);
      } else if (destination === 'tivly') {
        // User redirected to Tivly
        if (redirectUrl) {
          console.log('Redirecting to Tivly:', redirectUrl);
          window.location.href = redirectUrl;
        } else {
          console.error('No redirectUrl provided for tivly destination');
          setIsRedirecting(false);
          handleRedirectError(new Error('Missing redirect URL for Tivly'));
        }
      }
    } catch (error: unknown) {
      setIsRedirecting(false);

      // Enhanced error handling for different status codes
      const errorWithStatus = error as { status?: number };
      if (errorWithStatus?.status === 400) {
        console.error('Bad request - Invalid state or token');
      } else if (errorWithStatus?.status === 401) {
        console.error('Unauthorized - Invalid or expired token');
      } else if (errorWithStatus?.status === 500) {
        console.error('Server error - Please try again later');
      }

      handleRedirectError(error);
    }
  };

  // Loading state
  if (isLoading || redirectLoading || isRedirecting) {
    return <LoadingTruck text="Redirecting..." />;
  }

  // Error state
  if (error) {
    return (
      <View style={styles.container}>
        <Typography variant="h2">Error Fetching data...</Typography>
        {companyErrorToast && (
          <Toast
            type="error"
            text="An error occurred while fetching company details. Please try again!"
            animate={true}
          />
        )}
        {userErrorToast && (
          <Toast
            type="error"
            text="An error occurred while fetching user details. Please try again!"
            animate={true}
          />
        )}
      </View>
    );
  }

  // Show the DOT number input screen
  if (currentScreen === 'dotNumberInput') {
    return (
      <DotNumberScreen
        dotNumber={dotNumberInput}
        onChangeDot={setDotNumberInput}
        onNext={handleDotNumberSubmit}
        onNoDot={handleNoDotSubmit}
      />
    );
  }

  // Show company landing screen
  if (currentScreen === 'landing') {
    return (
      <>
        <CompanyLandingScreen />
        {leadModalVisible && (
          <LeadScoreModal
            leadModalVisible={leadModalVisible}
            setLeadModalVisible={setLeadModalVisible}
          />
        )}
      </>
    );
  }

  // Show company details screen
  if (currentScreen === 'details') {
    return <CompanyDetailsScreen />;
  }

  // Show garaging address screen
  if (currentScreen === 'garagingAddress') {
    return (
      <GaragingAddressScreen onSubmit={handleGaragingAddressSubmit} isLoading={isRedirecting} />
    );
  }

  // Show second user screen
  if (secondUserModalVisible) {
    return <SecondUserScreen dotNumber={userInfo?.company?.dot ?? 'N/A'} />;
  }

  // Default loading state (should not normally reach here)
  return <LoadingTruck text="Loading..." />;
};

export default CompanyStartScreen;
